import type { ApiModule, ApiSubModule } from '@/contexts/ModulesContext';
import type React from 'react';

// Map API module/submodule keys to routes and labels/icons
// Only include routes that exist in current app; unknown keys are ignored
import {
  LayoutDashboard,
  Building,
  Users,
  MapPin,
  Truck,
  CreditCard,
  TestTube,
  Upload,
  FileText,
  Brain,
  Bot,
  Zap,
  BarChart3,
  TrendingUp,
  Lock,
  Link,
  Code,
  Database,
  HeadphonesIcon,
  Timer,
  Settings,
} from 'lucide-react';

type IconType = React.ComponentType<{ className?: string }>;

export type NavItem = { title: string; url: string; icon: IconType; permission?: string; enabled?: boolean };
export type NavGroup = { title: string; items: NavItem[] };

const moduleKeyToTitle: Record<string, string> = {
  Dashboard: 'Dashboard',
  CustomerManagement: 'Customer Management',
  BillCollection: 'Bill Collection',
  ParserWorkflows: 'Parser Workflows',
  AIAutomation: 'AI & Automation',
  ReportsAnalytics: 'Reports & Analytics',
  IntegrationAPI: 'Integration & API',
  StorageSupport: 'Storage & Support',
  WorkspaceAdmin: 'Workspace Admin',
  Sandbox: 'Sandbox',
};

const subModuleKeyToConfig: Record<string, { title: string; path: string; icon: IconType }> = {
  // Customer Management
  Partners: { title: 'Partners', path: 'partners', icon: Building },
  Customers: { title: 'Customers', path: 'customers', icon: Users },
  Sites: { title: 'Sites', path: 'sites', icon: MapPin },
  Suppliers: { title: 'Suppliers', path: 'suppliers', icon: Truck },
  Accounts: { title: 'Accounts', path: 'accounts', icon: CreditCard },
  Identifiers: { title: 'Identifiers', path: 'identifiers', icon: Users },

  // Bill Collection
  ManualUpload: { title: 'Manual Upload', path: 'manual-upload', icon: Upload },
  Bills: { title: 'Bills', path: 'bills', icon: FileText },

  // Parser Workflows
  // No dedicated route exists for Manual Parser; route to parser-sandbox as closest feature
  ManualParser: { title: 'Manual Parser', path: 'parser-sandbox', icon: TestTube },

  // Sandbox
  CollectionSandbox: { title: 'Collection Sandbox', path: 'collector-sandbox', icon: TestTube },
  ParserSandbox: { title: 'Parser Sandbox', path: 'parser-sandbox', icon: TestTube },

  // AI & Automation
  AIParser: { title: 'AI Parser', path: 'ai-parser', icon: Brain },
  AIAgents: { title: 'AI Agents', path: 'ai-agents', icon: Bot },
  Automation: { title: 'Automation', path: 'automation', icon: Zap },

  // Reports & Analytics
  Reports: { title: 'Reports', path: 'reports', icon: BarChart3 },
  Analytics: { title: 'Analytics', path: 'analytics', icon: TrendingUp },
  EnterpriseAnalytics: { title: 'Enterprise Analytics', path: 'enterprise-analytics', icon: TrendingUp },
  Compliance: { title: 'Compliance', path: 'compliance', icon: Lock },

  // Integration & API
  Integrations: { title: 'Integrations', path: 'integrations', icon: Link },
  APIAccess: { title: 'API Access', path: 'api', icon: Code },

  // Storage & Support
  EnhancedStorage: { title: 'Enhanced Storage', path: 'storage', icon: Database },
  PrioritySupport: { title: 'Priority Support', path: 'support', icon: HeadphonesIcon },

  // Workspace Admin
  TrialManagement: { title: 'Trial Management', path: 'trial', icon: Timer },
  AccessControlNew: { title: 'Access Control (New)', path: 'access-control-new', icon: Lock },
  AccessControl: { title: 'Access Control', path: 'access-control', icon: Lock },
  BulkOperations: { title: 'Bulk Operations', path: 'bulk-operations', icon: Zap },
  AuditLogs: { title: 'Audit Logs', path: 'audit-logs', icon: FileText },
  SecurityMonitoring: { title: 'Security Monitoring', path: 'security-monitoring', icon: Lock },
  RoleManagement: { title: 'Role Management', path: 'role-management', icon: Users },
  MultiTenant: { title: 'Multi-Tenant', path: 'multi-tenant', icon: Building },
  WorkspaceSettings: { title: 'Workspace Settings', path: 'workspace-settings', icon: Settings },
};

export function buildNavFromModules(modules: ApiModule[] | undefined, basePrefix: string): NavGroup[] {
  if (!modules || modules.length === 0) return [];

  const groups: NavGroup[] = [];

  for (const m of modules) {
  const title = m.moduleName || moduleKeyToTitle[m.moduleKey] || m.moduleKey;

    // Special Dashboard module: direct link
    if (m.moduleKey === 'Dashboard') {
      groups.push({
        title: title || 'Dashboard',
        items: [
          { title: title || 'Dashboard', url: `${basePrefix}`, icon: LayoutDashboard, enabled: !!m.hasAccess },
        ],
      });
      continue;
    }

    // For other modules, build from submodules
    const items: NavItem[] = [];
    for (const sm of (m.subModules || [])) {
      const cfg = subModuleKeyToConfig[sm.subModuleKey];
      if (!cfg) continue; // unknown submodule
      const enabled = !!m.hasAccess && !!sm.hasAccess;
      items.push({ title: sm.subModuleName || cfg.title, url: `${basePrefix}/${cfg.path}`, icon: cfg.icon, enabled });
    }
    if (items.length > 0) {
      groups.push({ title, items });
    }
  }

  return groups;
}
